import React, { useState, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase';

const AddRequestModal = ({ 
  showAddRequestModal, 
  setShowAddRequestModal, 
  newRequest, 
  setNewRequest, 
  handleAddRequest, 
  saving 
}) => {
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (files) => {
    setUploadingImages(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const storageRef = ref(storage, `requests/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return {
          url: downloadURL,
          name: file.name,
          ref: snapshot.ref
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      setImages(prev => [...prev, ...uploadedImages]);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error uploading images. Please try again.');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageToDelete) => {
    try {
      await deleteObject(imageToDelete.ref);
      setImages(prev => prev.filter(img => img.url !== imageToDelete.url));
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error deleting image. Please try again.');
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleImageUpload(files);
    }
  };

  const handleAddRequestWithImages = async () => {
    // Update newRequest with image URLs or default placeholder
    const requestWithImages = {
      ...newRequest,
      images: images.length > 0 ? images.map(img => img.url) : ['https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=600&q=80']
    };
    
    // Call the parent's handleAddRequest with the updated request
    await handleAddRequest(requestWithImages);
    
    // Clear images after successful submission
    setImages([]);
  };

  if (!showAddRequestModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Add New Request</h3>
          <button
            onClick={() => setShowAddRequestModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={newRequest.title}
              onChange={(e) => setNewRequest(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Need 450W Solar Panels"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              rows="3"
              value={newRequest.description}
              onChange={(e) => setNewRequest(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what you need..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget *</label>
              <input
                type="text"
                value={newRequest.budget}
                onChange={(e) => setNewRequest(prev => ({ ...prev, budget: e.target.value }))}
                placeholder="₦50,000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <input
                type="text"
                value={newRequest.location}
                onChange={(e) => setNewRequest(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Lagos"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={newRequest.category}
              onChange={(e) => {
                const value = e.target.value;
                setNewRequest(prev => ({ ...prev, category: value }));
                setShowCustomCategory(value === 'custom');
                if (value !== 'custom') {
                  setCustomCategory('');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
            >
              <option value="solar-panels">Solar Panels</option>
              <option value="inverters">Inverters</option>
              <option value="batteries">Batteries</option>
              <option value="charge-controllers">Charge Controllers</option>
              <option value="solar-freezers">Solar Freezers</option>
              <option value="street-lights">Street Lights</option>
              <option value="installation">Installation Services</option>
              <option value="maintenance">Maintenance Services</option>
              <option value="custom">Custom Category</option>
            </select>
          </div>
          
          {showCustomCategory && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Category</label>
              <input
                type="text"
                value={customCategory}
                onChange={(e) => {
                  setCustomCategory(e.target.value);
                  setNewRequest(prev => ({ ...prev, category: e.target.value }));
                }}
                placeholder="Enter your custom category"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-abiu-green"
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Images (Optional - Upload 2-3 images)</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImages || images.length >= 3}
              className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-abiu-green focus:outline-none focus:ring-2 focus:ring-abiu-green transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingImages ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-abiu-green border-t-transparent rounded-full animate-spin mr-2"></div>
                  Uploading...
                </div>
              ) : (
                <div className="text-center">
                  <svg className="w-6 h-6 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    {images.length === 0 ? 'Click to upload images (optional)' : `Upload more images (${images.length}/3)`}
                  </span>
                </div>
              )}
            </button>
            
            {images.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-500">Uploaded Images:</p>
                <div className="grid grid-cols-2 gap-2">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.url}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeleteImage(image)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleAddRequestWithImages}
            disabled={saving || !newRequest.title || !newRequest.description || !newRequest.budget || !newRequest.location}
            className="flex-1 bg-abiu-green text-white py-2 rounded-lg font-semibold hover:bg-abiu-green-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Adding...' : 'Add Request'}
          </button>
          <button
            onClick={() => setShowAddRequestModal(false)}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRequestModal; 